import React from 'react';
import { isPlatform } from '@ionic/react';
import { getDownloadURL, getStorage, ref } from 'firebase/storage';
import { Functions, getFunctions, httpsCallable } from 'firebase/functions';
import { Case } from '../services/buildfire/rdb/cases';
import { Report } from '../services/buildfire/rdb/reports';
import { useFirebase } from '../services/firebase';
import { useDialog } from './DialogContext';
import ModalLoader from '../components/common/ModalLoader';
import { addDebugMessage } from '../components/Debug';
import { getErrorMessage } from '../util/error';
import {
  downloadReportInBrowser,
  getReportFileName,
  openSavedReport,
  saveReportToDevice,
} from '../services/reports/reportDownload';

type DownloadStage = 'idle' | 'generating' | 'saving';

const PROGRESS_TEXT: Record<Exclude<DownloadStage, 'idle'>, string> = {
  generating: 'Generating report…',
  saving: 'Saving to your device…',
};

interface IDownloadReportContext {
  downloadReport: (c: Case, report?: Report) => Promise<void>;
  isDownloading: boolean;
}

interface GenerateReportPDFRequest {
  uid: string;
  caseId: string;
  submitter: { email: string };
}

interface GenerateReportPDFResponse {
  url: string;
}

const DownloadReportContext = React.createContext<IDownloadReportContext>(
  undefined as any
);

const generateReportPDF = async (fns: Functions, c: Case): Promise<string> => {
  const fn = httpsCallable<GenerateReportPDFRequest, GenerateReportPDFResponse>(
    fns,
    'generateReportPDF'
  );
  const { data } = await fn({
    uid: c.uid,
    caseId: c.id,
    submitter: {
      email: c.user.email,
    },
  });
  return data.url;
};

export const DownloadReportProvider: React.FC = ({ children }) => {
  const [stage, setStage] = React.useState<DownloadStage>('idle');
  // Cancelling and restarting must not let the abandoned run resolve into the
  // new one's UI, so each run holds a token rather than a shared flag.
  const activeRun = React.useRef(0);
  const { openDialog } = useDialog();
  const { app } = useFirebase();

  const downloadReport = async (c: Case, report?: Report) => {
    const isDevice = isPlatform('android') || isPlatform('ios');
    const run = (activeRun.current += 1);
    const isStale = () => activeRun.current !== run;

    try {
      const fileName = getReportFileName(c);
      setStage('generating');

      let signedUrl: string;
      if (report?.fileName) {
        const r = ref(getStorage(app), report.fileName);
        signedUrl = await getDownloadURL(r);
      } else {
        signedUrl = await generateReportPDF(getFunctions(app), c);
      }

      if (isStale()) {
        return;
      }

      if (!isDevice) {
        downloadReportInBrowser(signedUrl, fileName);
        return;
      }

      setStage('saving');
      addDebugMessage({ content: ['Saving report to device', { fileName }] });

      const filePath = await saveReportToDevice(signedUrl, fileName);
      if (isStale()) {
        return;
      }

      await openSavedReport(filePath);
    } catch (e) {
      addDebugMessage({ content: e });
      console.error(e);
      openDialog(<div>{getErrorMessage(e as Error)}</div>);
    } finally {
      if (!isStale()) {
        setStage('idle');
      }
    }
  };

  const cancel = () => {
    activeRun.current += 1;
    setStage('idle');
  };

  return (
    <DownloadReportContext.Provider
      value={{ downloadReport, isDownloading: stage !== 'idle' }}
    >
      {children}
      {stage !== 'idle' && (
        <ModalLoader onClose={cancel} progressText={PROGRESS_TEXT[stage]} />
      )}
    </DownloadReportContext.Provider>
  );
};

export function useDownloadReport(): IDownloadReportContext {
  return React.useContext(DownloadReportContext);
}

export default DownloadReportContext;
